/**
 * useDocumentForm Hook
 * Manages document form state and changes
 * Extracted from DocumentDialog for reusability and testability
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Document } from '../../api/ApiClient';
import { DocumentFormData } from '../../components/documents/DocumentDialog/types';

export interface UseDocumentFormOptions {
  editingDoc: Document | null;
  defaultProvider: string;
  fullDocument?: any;
  selectedFile: File | null;
}

export interface UseDocumentFormReturn {
  formState: DocumentFormData;
  handleFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (e: any) => void;
  handleVisibilityChange: (visibility: string) => void;
  handleSharingChange: (userIds: string, groupIds: string) => void;
  setFormState: React.Dispatch<React.SetStateAction<DocumentFormData>>;
  /** Mark the title as user-provided (true) or free for auto-fill (false). */
  setTitleManuallyEdited: (edited: boolean) => void;
  resetForm: () => void;
}

/**
 * Custom hook for managing document form state
 */
export const useDocumentForm = ({
  editingDoc,
  defaultProvider,
  fullDocument,
  selectedFile
}: UseDocumentFormOptions): UseDocumentFormReturn => {
  const [formState, setFormState] = useState<DocumentFormData>({
    title: '',
    url: '',
    source_type: 'manual',
    content: '',
    visibility: 'private',
    shared_with_users: '',
    shared_with_groups: '',
    text_extraction_provider: defaultProvider
  });

  /**
   * Track if the user has manually edited the title field
   * This prevents auto-fill from overriding user's intentional changes
   */
  const titleManuallyEditedRef = useRef(false);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    // A fresh dialog starts without a manual title, so the next file
    // selection may fill the title again.
    titleManuallyEditedRef.current = false;
    if (editingDoc) {
      setFormState({
        title: editingDoc.title || '',
        url: editingDoc.url || '',
        source_type: editingDoc.source_type,
        content: (fullDocument as any)?.content || (fullDocument as any)?.text || '',
        visibility: editingDoc.visibility || 'private',
        shared_with_users: editingDoc.shared_with_users?.join(', ') || '',
        shared_with_groups: editingDoc.shared_with_groups?.join(', ') || '',
        text_extraction_provider: defaultProvider
      });
    } else {
      setFormState({
        title: '',
        url: '',
        source_type: 'manual',
        content: '',
        visibility: 'private',
        shared_with_users: '',
        shared_with_groups: '',
        text_extraction_provider: defaultProvider
      });
    }
  }, [editingDoc, defaultProvider, fullDocument]);

  /**
   * Initialize form when editing doc changes or full document loads
   */
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  /**
   * Reset the manual-edit flag when a file is replaced in edit mode, so the
   * title follows the new file name. In create mode a title the user typed
   * (for example on another tab) is kept when a file is added afterwards.
   */
  useEffect(() => {
    if (selectedFile && editingDoc) {
      titleManuallyEditedRef.current = false;
    }
  }, [selectedFile, editingDoc]);

  /**
   * Auto-fill the title from the selected file's name when the user hasn't
   * manually edited the title. This now also applies in EDIT mode: when a file
   * is replaced via "Datei aendern", the title follows the new filename instead
   * of keeping the old document's name (unless the user typed a custom title).
   * The `selectedFile` dependency drives the update; we intentionally do NOT
   * depend on formState.title so a pre-filled (old) title doesn't block it.
   */
  useEffect(() => {
    if (selectedFile && !titleManuallyEditedRef.current) {
      const fileName = selectedFile.name.split('.').slice(0, -1).join('.');
      if (fileName) {
        setFormState(prev => ({
          ...prev,
          title: fileName,
        }));
      }
    }
     
  }, [selectedFile]);

  /**
   * Handle form input changes
   */
  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Mark title as manually edited when user changes it
    if (name === 'title') {
      titleManuallyEditedRef.current = true;
    }

    // Auto-fill title from URL if empty and URL is provided
    if (name === 'url' && value && !formState.title) {
      try {
        const url = new URL(value);
        let title = url.pathname.split('/').pop() || url.hostname;
        title = title.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        if (title && title !== url.hostname) {
          setFormState(prev => ({
            ...prev,
            [name]: value,
            title: title
          }));
          return;
        }
      } catch (error) {
        // Invalid URL, ignore
      }
    }

    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  }, [formState.title]);

  /**
   * Handle select input changes
   */
  const handleSelectChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  /**
   * Handle visibility changes
   */
  const handleVisibilityChange = useCallback((visibility: string) => {
    setFormState(prev => ({
      ...prev,
      visibility
    }));
  }, []);

  /**
   * Handle sharing changes
   */
  const handleSharingChange = useCallback((userIds: string, groupIds: string) => {
    setFormState(prev => ({
      ...prev,
      shared_with_users: userIds,
      shared_with_groups: groupIds
    }));
  }, []);

  const setTitleManuallyEdited = useCallback((edited: boolean) => {
    titleManuallyEditedRef.current = edited;
  }, []);

  return {
    formState,
    handleFormChange,
    handleSelectChange,
    handleVisibilityChange,
    handleSharingChange,
    setFormState,
    setTitleManuallyEdited,
    resetForm
  };
};
